<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BulkRentalServiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    items: {
      type: Array,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItemFromInput = (...args) => {
    const [inputItems, fields] = args;
    const fieldNames = _.map(fields, 'name');

    let result = {};

    _.each(inputItems, (item, i) => {
      if (i === 0) {
        result = _.pick(item, fieldNames);
      }

      for (const { name, defaultValue } of fields) {
        if (result[name] != item[name]) {
          result[name] = defaultValue;
          continue;
        }

        if (_.isUndefined(result[name])) {
          result[name] = defaultValue;
          continue;
        }

        if (result[name] != null) {
          result[name] = item[name];
        }
      }
    });

    result['$updates'] = _.reduce(fieldNames, (m, v) => _.set(m, v, false), {});
    return result;
  };
  const getPayload = (...args) => {
    const [inputItems, item] = args;

    const updates = _.keys(_.pickBy(item.$updates, _.identity));

    if (!updates.length) {
      return;
    }

    const update = _.pick(item, updates);

    return {
      items: _.map(inputItems, (item) => ({
        id: item.id,
        etag: item.etag,
        value: update,
      })),
    };
  };

  const editableFields = ref([
    {
      name: 'service',
    },
    {
      name: 'name',
    },
    {
      name: 'billing_unit',
    },
    {
      name: 'invoice_notes',
    },
    {
      name: 'invoice_notes',
    },
    {
      name: 'valid_from',
    },
    {
      name: 'valid_to',
    },
    {
      name: 'tariff_code',
    },
    {
      name: 'meter_factor',
    },
    {
      name: 'intermediated_service',
    },
    {
      name: 'inflation_indexed',
    },
    {
      name: 'notes',
    },
  ]);
  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BulkRentalServiceEditor');
  const item = ref(null);
  const services = ref(null);
  const tariffs = ref(null);
  const units = ref(null);
  const invoiceGroups = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_3468003412020058_dataSource = computed(() => ({
    name: 'suggest_product',
    parameters: {
      product_types: 'service',
    },
  }));

  const $data = computed(() => ({
    editableFields: editableFields.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    services: services.value,
    tariffs: tariffs.value,
    units: units.value,
    invoiceGroups: invoiceGroups.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_3468003412020058_dataSource: b_3468003412020058_dataSource.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return getItemFromInput(props.items, $data.value.editableFields);
  };
  const $$load_services = () => {
    return $fn.value
      .fetch('suggest_product', {
        parameters: {
          query: '*',
          top: 50,
          skip: 0,
          product_types: 'service',
          product_invoice_groups: 'all',
        },
      })
      .then((data) => _.orderBy(data, [(p) => p.name.toLowerCase()]));
  };
  const $$load_tariffs = () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'tariff' },
    });
  };
  const $$load_units = () => {
    return $fn.value
      .fetch('list_unit')
      .then((d) => _.orderBy(d, [(p) => p.symbol.toLowerCase()]));
  };
  const $$load_invoiceGroups = () => {
    return $fn.value.fetch('list_product_invoice_group');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'editableFields' },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'services' },
      { name: 'tariffs' },
      { name: 'units' },
      { name: 'invoiceGroups' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_3468003412020058_dataSource', nodeId: '3468003412020058' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      services: $$load_services,
      tariffs: $$load_tariffs,
      units: $$load_units,
      invoiceGroups: $$load_invoiceGroups,
    },
    ux: {},
    dataRefs: {
      editableFields,
      labelAlign,
      formId,
      formName,
      item,
      services,
      tariffs,
      units,
      invoiceGroups,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_3468003412020058_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BulkRentalServiceEditor',
        'Edit Service Properties'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_74sd6dv50_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_vuvd6davb_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_request_twe20avxr_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_rental_service',
      data: getPayload(props.items, item.value),
    };
  };
  const act_3468003412020058_setData_08nb9sx4j_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.service_id',
      value: item.value.service.id,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3468003412020058_setData_hg68gicce_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.name',
      value: $event.data.option.name,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_closeModal_74sd6dv50 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_74sd6dv50_getActionArgs,
    });
  const inst_evh_7315092382398562_close_afvhuzyrx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_74sd6dv50,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_afvhuzyrx],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_zr5i7oj8v =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_gq0hlapvk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_zr5i7oj8v,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_gq0hlapvk],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_lmek42k50 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_3xomthgwt =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_lmek42k50,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_3xomthgwt],
    event: 'close',
  });
  const act_1040167445267876_closeModal_vuvd6davb =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_vuvd6davb_getActionArgs,
    });
  const inst_evh_1040167445267876_success_wdddje7gy =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_vuvd6davb,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_twe20avxr = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_twe20avxr_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_wdddje7gy],
  });
  const inst_evh_1040167445267876_submit_cw11urn1r =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_twe20avxr,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_cw11urn1r],
    event: 'submit',
  });
  const act_3468003412020058_setData_08nb9sx4j = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_08nb9sx4j_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_soe38k635 =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_08nb9sx4j,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const act_3468003412020058_setData_hg68gicce = new actionTypes.SetDataAction({
    actionArgs: act_3468003412020058_setData_hg68gicce_getActionArgs,
  });
  const inst_evh_3468003412020058_optionSelected_b1talkso1 =
    new actionTypes.EventHandlerImpl({
      action: act_3468003412020058_setData_hg68gicce,
      event: 'optionSelected',
      displayName: 'setData',
    });
  const inst_evh_3468003412020058_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [
        inst_evh_3468003412020058_optionSelected_soe38k635,
        inst_evh_3468003412020058_optionSelected_b1talkso1,
      ],
      event: 'optionSelected',
    });
  const act_2434514588044777_closeComponent_9y8pfaty0 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_wmbep62cd =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_9y8pfaty0,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_wmbep62cd],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_afvhuzyrx,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_gq0hlapvk,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_3xomthgwt,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_wdddje7gy,
    inst_evh_1040167445267876_submit_cw11urn1r,
    save: inst_evh_1040167445267876_submit_cw11urn1r,
    inst_evh_1040167445267876_submit,
    inst_evh_3468003412020058_optionSelected_soe38k635,
    inst_evh_3468003412020058_optionSelected_b1talkso1,
    inst_evh_3468003412020058_optionSelected,
    inst_evh_2434514588044777_clickPreventStop_wmbep62cd,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_afvhuzyrx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_afvhuzyrx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_gq0hlapvk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_gq0hlapvk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_3xomthgwt = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_3xomthgwt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_wdddje7gy = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_wdddje7gy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_cw11urn1r = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_cw11urn1r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_soe38k635 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_soe38k635.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected_b1talkso1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3468003412020058_optionSelected_b1talkso1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3468003412020058_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3468003412020058_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_wmbep62cd = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_wmbep62cd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BulkRentalServiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="
          pgettext('BulkRentalServiceEditor|ResourceTitle', 'Rental Services')
        "
        :resourceKey="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="service"
          :label="pgettext('RentalService', 'Service')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="service_enabled"
                v-model="item.$updates.service"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormSearchableSelect
            name="service"
            v-model="item.service"
            :dataSource="b_3468003412020058_dataSource"
            bind-object
            valueField="id"
            textField="name"
            :disabled="!item.$updates.service"
            required
            @optionSelected="evh_3468003412020058_optionSelected($event, {})"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="gettext('Valid From')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="valid_from_enabled"
                v-model="item.$updates.valid_from"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormDatetime
            name="valid_from"
            v-model="item.valid_from"
            :disabled="!item.$updates.valid_from"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_to"
          :label="gettext('Valid To')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="valid_to_enabled"
                v-model="item.$updates.valid_to"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormDatetime
            name="valid_to"
            v-model="item.valid_to"
            :disabled="!item.$updates.valid_to"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="intermediated_service"
          :label="gettext('Intermediated service')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="intermediated_service_enabled"
                v-model="item.$updates.intermediated_service"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormCheckbox
            type="number"
            name="intermediated_service"
            v-model="item.intermediated_service"
            :disabled="!item.$updates.intermediated_service"
            class="w-auto"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="inflation_indexed"
          :label="gettext('Inflation indexed')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="inflation_indexed_enabled"
                v-model="item.$updates.inflation_indexed"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormCheckbox
            type="number"
            name="inflation_indexed"
            v-model="item.inflation_indexed"
            :disabled="!item.$updates.inflation_indexed"
            class="w-auto"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="tariff_code"
          :label="pgettext('RentalService', 'Tariff')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          v-if="tariffs && tariffs.length"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="tariff_code_enabled"
                v-model="item.$updates.tariff_code"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormSearchableSelect
            name="tariff_code"
            v-model="item.tariff_code"
            :options="tariffs"
            clearable
            valueField="code"
            textField="code,name"
            :disabled="!item.$updates.tariff_code"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_unit"
          :label="pgettext('RentalService', 'Billing unit')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="billing_unit_enabled"
                v-model="item.$updates.billing_unit"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormSearchableSelect
            name="billing_unit"
            v-model="item.billing_unit"
            :options="units"
            valueField="symbol"
            textField="symbol"
            :disabled="!item.$updates.billing_unit"
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <hr class="my-4" />
        <ItpFormGroup
          name="notes"
          :label="pgettext('RentalService', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="notes_enabled"
                v-model="item.$updates.notes"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :disabled="!item.$updates.notes"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
